import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { CaretLeft, CaretRight } from '@liveauctioneers/hammer-ui-core/icons';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Lightbox from 'react-spring-lightbox';

import { LightboxHeader as Header } from '@/components/Lightbox/Header/Header';
import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';
import { useTrackItemImageGalleryInteractionAnalytics } from '@/utils/amplitude/analytics/item/trackItemImageGalleryInteraction.analytics';

import useConsoleCarouselData from './hooks/useConsoleCarouselData';

export type RouteParams = {
    catalogId: string;
    imageNum: string;
    itemId: string;
};

const ImageCarouselPopoutPage = () => {
    const {
        catalogId: catalogIdString,
        imageNum: imageNumWhenPoppedOut,
        itemId: itemIdString,
    } = useParams<RouteParams>();

    const catalogId = Number(catalogIdString);
    const itemId = Number(itemIdString);
    const initialIndex = Number(imageNumWhenPoppedOut) - 1;

    const { canNext, canPrev, currentImageIndex, gotoNext, gotoPrevious, mappedPhotos, title } = useConsoleCarouselData(
        { catalogId, initialIndex, itemId }
    );
    const trackImageGalleryInteraction = useTrackItemImageGalleryInteractionAnalytics();
    const totalImages = mappedPhotos.length;

    const trackLightboxNavigation = useCallback(
        (nextIndex: number, interactionType: 'Next' | 'Prev') => {
            if (!totalImages) {
                return;
            }
            trackImageGalleryInteraction({
                imagePosition: nextIndex + 1,
                interactionType,
                totalImages,
            });
        },
        [totalImages, trackImageGalleryInteraction]
    );

    const handleNext = useCallback(() => {
        const nextIndex = canNext ? currentImageIndex + 1 : 0;
        trackLightboxNavigation(nextIndex, 'Next');
        gotoNext();
    }, [canNext, currentImageIndex, gotoNext, trackLightboxNavigation]);

    const handlePrevious = useCallback(() => {
        const nextIndex = canPrev ? currentImageIndex - 1 : totalImages - 1;
        trackLightboxNavigation(nextIndex, 'Prev');
        gotoPrevious();
    }, [canPrev, currentImageIndex, gotoPrevious, totalImages, trackLightboxNavigation]);

    const isSsr = useIsServerSideRendered();

    if (isSsr) {
        return null;
    }

    return (
        <Lightbox
            currentIndex={currentImageIndex}
            images={mappedPhotos}
            isOpen
            onNext={handleNext}
            onPrev={handlePrevious}
            renderHeader={() => (
                <Header
                    currentImageIndex={currentImageIndex}
                    galleryLength={mappedPhotos.length}
                    galleryTitle={title}
                    itemId={itemId}
                    onClose={() => window.close()}
                />
            )}
            renderNextButton={() => (
                <IconButton
                    className="absolute right-sm z-10 xsMax:hidden"
                    data-testid="imageCarouselPopoutNextButton"
                    disabled={!canNext}
                    icon={CaretRight}
                    iconStyle="bold"
                    onClick={handleNext}
                    variant="outlined"
                />
            )}
            renderPrevButton={() => (
                <IconButton
                    className="absolute left-sm z-10 xsMax:hidden"
                    data-testid="imageCarouselPopoutPrevButton"
                    disabled={!canPrev}
                    icon={CaretLeft}
                    iconStyle="bold"
                    onClick={handlePrevious}
                    variant="outlined"
                />
            )}
            singleClickToZoom
        />
    );
};

export default ImageCarouselPopoutPage;
